import { useState, type KeyboardEvent } from "react";
import { Pencil, Trash2, Plus, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { EntityCombobox } from "@/components/ui/entity-combobox";
import { useCompanies } from "@/hooks/useCompanies";
import {
  useEquipmentInstances,
  useCreateEquipmentInstance,
  useUpdateEquipmentInstance,
  useDeleteEquipmentInstance,
  type EquipmentInstance,
  type EquipmentInstanceStatus,
} from "@/hooks/useEquipmentInstances";
import { useIsMobile } from "@/hooks/use-mobile";

type InstanceStatus = EquipmentInstanceStatus;

interface EquipmentInstanceTableProps {
  equipmentId: string;
}

const statusColors: Record<InstanceStatus, string> = {
  active: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  repair:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
  archived: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200",
};

export const EquipmentInstanceTable = ({
  equipmentId,
}: EquipmentInstanceTableProps) => {
  const { data: instances = [], isLoading } =
    useEquipmentInstances(equipmentId);
  const { data: companies = [] } = useCompanies();
  const createInstance = useCreateEquipmentInstance();
  const updateInstance = useUpdateEquipmentInstance();
  const deleteInstance = useDeleteEquipmentInstance();
  const isMobile = useIsMobile();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    identifier: "",
    serial_number: "",
    notes: "",
    acquisition_date: "",
    acquisition_price: "",
    vendor_id: "",
    status: "active" as InstanceStatus,
  });

  const companyOptions = companies.map((c) => ({
    value: c.id,
    label: c.name,
  }));

  const resetForm = () => {
    setFormData({
      identifier: "",
      serial_number: "",
      notes: "",
      acquisition_date: "",
      acquisition_price: "",
      vendor_id: "",
      status: "active",
    });
    setEditingId(null);
    setIsCreating(false);
    setIsDialogOpen(false);
  };

  const openCreateDialog = () => {
    setFormData({
      identifier: "",
      serial_number: "",
      notes: "",
      acquisition_date: "",
      acquisition_price: "",
      vendor_id: "",
      status: "active",
    });
    setEditingId(null);
    setIsCreating(true);
    if (isMobile) {
      setIsDialogOpen(true);
    }
  };

  const openEditDialog = (instance: EquipmentInstance) => {
    setEditingId(instance.id);
    setFormData({
      identifier: instance.identifier,
      serial_number: instance.serial_number || "",
      notes: instance.notes || "",
      acquisition_date: instance.acquisition_date || "",
      acquisition_price: instance.acquisition_price?.toString() || "",
      vendor_id: instance.vendor_id || "",
      status: instance.status,
    });
    setIsCreating(false);
    if (isMobile) {
      setIsDialogOpen(true);
    }
  };

  const startEdit = (instance: EquipmentInstance) => {
    setEditingId(instance.id);
    setFormData({
      identifier: instance.identifier,
      serial_number: instance.serial_number || "",
      notes: instance.notes || "",
      acquisition_date: instance.acquisition_date || "",
      acquisition_price: instance.acquisition_price?.toString() || "",
      vendor_id: instance.vendor_id || "",
      status: instance.status,
    });
  };

  const handleSave = () => {
    const data = {
      identifier: formData.identifier,
      serial_number: formData.serial_number || null,
      notes: formData.notes || null,
      acquisition_date: formData.acquisition_date || null,
      acquisition_price: formData.acquisition_price
        ? Number(formData.acquisition_price)
        : null,
      vendor_id: formData.vendor_id || null,
      status: formData.status,
    };

    if (isCreating) {
      createInstance.mutate(
        { ...data, equipment_id: equipmentId },
        { onSuccess: resetForm },
      );
    } else if (editingId) {
      updateInstance.mutate(
        { ...data, id: editingId, equipmentId },
        { onSuccess: resetForm },
      );
    }
  };

  const handleDelete = (id: string) => {
    if (confirm("Are you sure you want to delete this instance?")) {
      deleteInstance.mutate({ id, equipmentId });
    }
  };

  const handleInlineFormKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== "Enter") return;

    event.preventDefault();
    handleSave();
  };

  if (isLoading) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        Loading instances...
      </div>
    );
  }

  const isEditing = editingId !== null || isCreating;

  const renderFormFields = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="identifier">Identifier</Label>
        <Input
          id="identifier"
          value={formData.identifier}
          onChange={(e) =>
            setFormData({ ...formData, identifier: e.target.value })
          }
          placeholder="Identifier"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="serial_number">Serial Number</Label>
        <Input
          id="serial_number"
          value={formData.serial_number}
          onChange={(e) =>
            setFormData({ ...formData, serial_number: e.target.value })
          }
          placeholder="Serial Number"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="status">Status</Label>
        <Select
          value={formData.status}
          onValueChange={(value: InstanceStatus) =>
            setFormData({ ...formData, status: value })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="repair">Repair</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="acquisition_date">Acquisition Date</Label>
        <DatePicker
          id="acquisition_date"
          value={formData.acquisition_date}
          onChange={(v) => setFormData({ ...formData, acquisition_date: v })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="acquisition_price">Price</Label>
        <Input
          id="acquisition_price"
          type="number"
          step="0.01"
          value={formData.acquisition_price}
          onChange={(e) =>
            setFormData({ ...formData, acquisition_price: e.target.value })
          }
          placeholder="Price"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="vendor">Vendor</Label>
        <EntityCombobox
          options={companyOptions}
          value={formData.vendor_id}
          onChange={(value) =>
            setFormData({ ...formData, vendor_id: value || "" })
          }
          placeholder="Select vendor"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="notes">Notes</Label>
        <Input
          id="notes"
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="Notes"
        />
      </div>
    </div>
  );

  // Mobile view with cards and dialog
  if (isMobile) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">
            Instances ({instances.length})
          </h3>
          <Button offlineEdit onClick={openCreateDialog} size="sm">
            <Plus className="h-4 w-4 mr-2" />
            Add Instance
          </Button>
        </div>

        {instances.length === 0 ? (
          <div className="text-center text-muted-foreground py-8 border rounded-lg">
            No instances yet. Click "Add Instance" to create one.
          </div>
        ) : (
          <div className="space-y-2">
            {instances.map((instance) => (
              <Card
                key={instance.id}
                className="cursor-pointer"
                onClick={() => openEditDialog(instance)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="font-medium">{instance.identifier}</p>
                      <div className="flex items-center gap-2">
                        <Badge className={statusColors[instance.status]}>
                          {instance.status}
                        </Badge>
                        {instance.acquisition_price && (
                          <span className="text-sm text-muted-foreground">
                            €{instance.acquisition_price}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(instance.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Dialog
          open={isDialogOpen}
          onOpenChange={(open) => {
            if (!open) resetForm();
          }}
        >
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {isCreating ? "Add Instance" : "Edit Instance"}
              </DialogTitle>
            </DialogHeader>
            {renderFormFields()}
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={resetForm}>
                Cancel
              </Button>
              <Button offlineEdit onClick={handleSave}>
                {isCreating ? "Create" : "Save"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Desktop view with table and inline editing
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">
          Instances ({instances.length})
        </h3>
        <Button
          onClick={openCreateDialog}
          disabled={isEditing}
          size="sm"
          data-testid="add-equipment-instance"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Instance
        </Button>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Identifier</TableHead>
              <TableHead className="min-w-[240px]">Serial Number</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Acquisition Date</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Vendor</TableHead>
              <TableHead>Notes</TableHead>
              <TableHead className="w-20">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isCreating && (
              <TableRow>
                <TableCell>
                  <Input
                    value={formData.identifier}
                    data-testid="equipment-instance-identifier-input"
                    onKeyDown={handleInlineFormKeyDown}
                    onChange={(e) =>
                      setFormData({ ...formData, identifier: e.target.value })
                    }
                    placeholder="Identifier"
                  />
                </TableCell>
                <TableCell>
                  <Input
                    value={formData.serial_number}
                    data-testid="equipment-instance-serial-input"
                    onKeyDown={handleInlineFormKeyDown}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        serial_number: e.target.value,
                      })
                    }
                    placeholder="Serial Number"
                    className="min-w-[220px]"
                  />
                </TableCell>
                <TableCell>
                  <Select
                    value={formData.status}
                    onValueChange={(value: InstanceStatus) =>
                      setFormData({ ...formData, status: value })
                    }
                  >
                    <SelectTrigger className="w-28">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="repair">Repair</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <DatePicker
                    value={formData.acquisition_date}
                    onChange={(v) =>
                      setFormData({
                        ...formData,
                        acquisition_date: v,
                      })
                    }
                  />
                </TableCell>
                <TableCell>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.acquisition_price}
                    data-testid="equipment-instance-price-input"
                    onKeyDown={handleInlineFormKeyDown}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        acquisition_price: e.target.value,
                      })
                    }
                    placeholder="Price"
                    className="w-24"
                  />
                </TableCell>
                <TableCell>
                  <EntityCombobox
                    options={companyOptions}
                    value={formData.vendor_id}
                    onChange={(value) =>
                      setFormData({ ...formData, vendor_id: value || "" })
                    }
                    placeholder="Select vendor"
                  />
                </TableCell>
                <TableCell>
                  <Input
                    value={formData.notes}
                    data-testid="equipment-instance-notes-input"
                    onKeyDown={handleInlineFormKeyDown}
                    onChange={(e) =>
                      setFormData({ ...formData, notes: e.target.value })
                    }
                    placeholder="Notes"
                  />
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={handleSave}
                      data-testid="save-equipment-instance"
                    >
                      <Check className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={resetForm}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            )}
            {instances.map((instance) =>
              editingId === instance.id ? (
                <TableRow key={instance.id}>
                  <TableCell>
                    <Input
                      value={formData.identifier}
                      data-testid="equipment-instance-identifier-input"
                      onKeyDown={handleInlineFormKeyDown}
                      onChange={(e) =>
                        setFormData({ ...formData, identifier: e.target.value })
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={formData.serial_number}
                      data-testid="equipment-instance-serial-input"
                      onKeyDown={handleInlineFormKeyDown}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          serial_number: e.target.value,
                        })
                      }
                      className="min-w-[220px]"
                    />
                  </TableCell>
                  <TableCell>
                    <Select
                      value={formData.status}
                      onValueChange={(value: InstanceStatus) =>
                        setFormData({ ...formData, status: value })
                      }
                    >
                      <SelectTrigger className="w-28">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="repair">Repair</SelectItem>
                        <SelectItem value="archived">Archived</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <DatePicker
                      value={formData.acquisition_date}
                      onChange={(v) =>
                        setFormData({
                          ...formData,
                          acquisition_date: v,
                        })
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.acquisition_price}
                      data-testid="equipment-instance-price-input"
                      onKeyDown={handleInlineFormKeyDown}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          acquisition_price: e.target.value,
                        })
                      }
                      className="w-24"
                    />
                  </TableCell>
                  <TableCell>
                    <EntityCombobox
                      options={companyOptions}
                      value={formData.vendor_id}
                      onChange={(value) =>
                        setFormData({ ...formData, vendor_id: value || "" })
                      }
                      placeholder="Select vendor"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={formData.notes}
                      data-testid="equipment-instance-notes-input"
                      onKeyDown={handleInlineFormKeyDown}
                      onChange={(e) =>
                        setFormData({ ...formData, notes: e.target.value })
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={handleSave}
                        data-testid="save-equipment-instance"
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={resetForm}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow
                  key={instance.id}
                  className={isEditing ? undefined : "cursor-pointer"}
                  data-testid={`equipment-instance-row-${instance.id}`}
                  onDoubleClick={() => {
                    if (!isEditing) {
                      startEdit(instance);
                    }
                  }}
                >
                  <TableCell
                    className="font-medium"
                    data-testid={`equipment-instance-identifier-${instance.id}`}
                  >
                    {instance.identifier}
                  </TableCell>
                  <TableCell className="min-w-[240px]">
                    {instance.serial_number || "-"}
                  </TableCell>
                  <TableCell>
                    <Badge className={statusColors[instance.status]}>
                      {instance.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{instance.acquisition_date || "-"}</TableCell>
                  <TableCell>
                    {instance.acquisition_price
                      ? `€${instance.acquisition_price}`
                      : "-"}
                  </TableCell>
                  <TableCell>{instance.vendor?.name || "-"}</TableCell>
                  <TableCell className="max-w-[200px] truncate">
                    {instance.notes || "-"}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => startEdit(instance)}
                        disabled={isEditing}
                        data-testid={`edit-equipment-instance-${instance.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleDelete(instance.id)}
                        disabled={isEditing}
                        data-testid={`delete-equipment-instance-${instance.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ),
            )}
            {instances.length === 0 && !isCreating && (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="text-center text-muted-foreground py-8"
                >
                  No instances yet. Click "Add Instance" to create one.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
